import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import GroupFeed from '../components/GroupFeed';
import EditGroupModal from '../components/EditGroupModal';
import GroupMembersModal from '../components/GroupMembersModal';

const Groups = () => {
  const { user } = useContext(AuthContext);
  const [groups, setGroups] = useState([]);
  const [myGroups, setMyGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [showFeed, setShowFeed] = useState(false);
  const [showGroupDetails, setShowGroupDetails] = useState(false);
  const [showEditGroup, setShowEditGroup] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [membersModalGroup, setMembersModalGroup] = useState(null);
  const [newGroup, setNewGroup] = useState({
    name: '',
    description: '',
    group_type: 'public',
    industry_tags: [],
    location: '',
    rules: ['']
  });

  useEffect(() => {
    fetchGroups();
    fetchMyGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.get(`${backendUrl}/api/groups`);
      setGroups(response.data);
      console.log('✅ Groups loaded successfully:', response.data.length, 'groups');
    } catch (error) {
      console.error('🚨 Error fetching groups:', error);
      console.log('🚨 DEBUG: Groups fetch failed with:');
      console.log('   Status:', error.response?.status);
      console.log('   Response Data:', error.response?.data);
      
      // Set empty array on error
      setGroups([]);
    }
  };

  const fetchMyGroups = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.get(`${backendUrl}/api/groups/my-groups`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setMyGroups(response.data);
      console.log('✅ My groups loaded successfully:', response.data.length, 'groups');
    } catch (error) {
      console.error('🚨 Error fetching my groups:', error);
      console.log('🚨 DEBUG: My groups fetch failed with:');
      console.log('   Status:', error.response?.status);
      console.log('   Response Data:', error.response?.data);
      
      // Set empty array on error
      setMyGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const joinGroup = async (groupId) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      await axios.post(`${backendUrl}/api/groups/${groupId}/join`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      // Refresh groups
      fetchGroups();
      fetchMyGroups();
    } catch (error) {
      console.error('Error joining group:', error);
    }
  };

  const createGroup = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.post(`${backendUrl}/api/groups`, newGroup, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setGroups([response.data, ...groups]);
      setMyGroups([response.data, ...myGroups]);
      setShowCreateGroup(false);
      setNewGroup({
        name: '',
        description: '',
        group_type: 'public',
        industry_tags: [],
        location: '',
        rules: ['']
      });
    } catch (error) {
      console.error('Error creating group:', error);
    }
  };

  const industryOptions = [
    'textile', 'manufacturing', 'fashion', 'electronics', 'retail', 'technology',
    'organic', 'food', 'sustainable', 'furniture', 'home', 'wood', 'spices'
  ];

  const handleIndustryTagChange = (tag) => {
    const currentTags = newGroup.industry_tags;
    if (currentTags.includes(tag)) {
      setNewGroup({
        ...newGroup,
        industry_tags: currentTags.filter(t => t !== tag)
      });
    } else {
      setNewGroup({
        ...newGroup,
        industry_tags: [...currentTags, tag]
      });
    }
  };

  // Group Feed Modal
  const openGroupFeed = (group) => {
    setSelectedGroup(group);
    setShowFeed(true);
  };

  const closeGroupFeed = () => {
    setShowFeed(false);
    setSelectedGroup(null);
  };

  // Group Details Modal
  const openGroupDetails = (group) => {
    setSelectedGroup(group);
    setShowGroupDetails(true);
  };

  const closeGroupDetails = () => {
    setShowGroupDetails(false);
    setSelectedGroup(null);
  };

  // Group Edit Modal
  const openGroupEdit = (group) => {
    setEditingGroup({
      id: group.id,
      name: group.name,
      description: group.description,
      group_type: group.group_type || 'public',
      industry_tags: group.industry_tags || [],
      location: group.location || '',
      rules: group.rules || ['']
    });
    setShowEditGroup(true);
  };

  const closeGroupEdit = () => {
    setShowEditGroup(false);
    setEditingGroup(null);
  };

  const handleEditGroup = async (updatedGroupData) => {
    try {
      console.log('🔄 Updating group:', updatedGroupData.id);
      
      const response = await axios.put(`/groups/${updatedGroupData.id}`, {
        name: updatedGroupData.name,
        description: updatedGroupData.description,
        group_type: updatedGroupData.group_type,
        industry_tags: updatedGroupData.industry_tags,
        location: updatedGroupData.location || null,
        rules: updatedGroupData.rules.filter(rule => rule.trim())
      });

      console.log('✅ Group updated successfully:', response.data);
      
      // Refresh groups data
      await fetchGroups();
      await fetchMyGroups();
      
      // Close modal
      closeGroupEdit();
      
      // Show success message (you can replace this with a toast notification)
      alert('Group updated successfully!');
      
    } catch (error) {
      console.error('❌ Error updating group:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to update group';
      alert(`Error updating group: ${errorMessage}`);
    }
  };

  // Check if user can edit a group (creator or admin)
  const canEditGroup = (group) => {
    return group.admin_user_id === user?.id || user?.role === 'admin' || user?.role === 'moderator';
  };

  const filteredGroups = groups.filter(group =>
    group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    group.industry_tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (group.location && group.location.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-300 rounded w-1/4"></div>
          <div className="grid md:grid-cols-2 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg shadow-sm p-6">
                <div className="h-6 bg-gray-300 rounded w-3/4 mb-4"></div>
                <div className="h-4 bg-gray-300 rounded w-full mb-2"></div>
                <div className="h-4 bg-gray-300 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Trade Groups</h1>
          <p className="text-gray-600">Discover and join business communities in your industry</p>
        </div>
        <button
          onClick={() => setShowCreateGroup(true)}
          className="mt-4 md:mt-0 bg-teal-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-teal-700 transition-colors"
        >
          + Create Group
        </button>
      </div>

      {/* Search */}
      <div className="mb-8">
        <div className="relative">
          <input
            type="text"
            placeholder="Search groups by name, industry, or location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-teal-500 focus:border-teal-500"
          />
          <span className="absolute left-4 top-3.5 text-gray-400">🔍</span>
        </div>
      </div>

      {/* My Groups */}
      {myGroups.length > 0 && (
        <div className="mb-12">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">My Groups</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {myGroups.map((group) => (
              <div key={group.id} className="bg-white rounded-lg shadow-sm border-l-4 border-teal-500">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-teal-100 text-teal-600 rounded-lg flex items-center justify-center font-bold text-xl">
                        {group.name.substring(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{group.name}</h3>
                        <div className="flex items-center space-x-2 text-sm text-teal-600">
                          <span className="w-2 h-2 bg-teal-500 rounded-full"></span>
                          <span>Active</span>
                        </div>
                      </div>
                    </div>
                    <span className="bg-teal-100 text-teal-800 px-2 py-1 rounded-full text-xs font-medium">
                      {group.user_role}
                    </span>
                  </div>
                  
                  <p className="text-gray-600 text-sm mb-4">{group.description}</p>
                  
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        console.log('🔍 Members button clicked for group:', group.name, group.id);
                        setMembersModalGroup(group);
                        setShowMembersModal(true);
                      }}
                      className="hover:text-teal-600 hover:underline transition-colors cursor-pointer"
                    >
                      👥 {group.member_count} members
                    </button>
                    <span>📍 {group.location}</span>
                  </div>
                  
                  <div className="mt-4 flex flex-wrap gap-2">
                    {group.industry_tags.slice(0, 3).map((tag, index) => (
                      <span key={index} className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs">
                        #{tag}
                      </span>
                    ))}
                  </div>
                  
                  <div className="mt-4 text-sm text-teal-600">
                    {group.posts_count} new posts this week
                  </div>
                  
                  {/* Group Actions */}
                  <div className="mt-4 flex space-x-2">
                    <button
                      onClick={() => openGroupFeed(group)}
                      className="flex-1 bg-teal-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors flex items-center justify-center space-x-2"
                    >
                      <span>💬</span>
                      <span>📰 Feed</span>
                    </button>
                    <button
                      onClick={() => openGroupDetails(group)}
                      className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                    >
                      View Details
                    </button>
                    {canEditGroup(group) && (
                      <button
                        onClick={() => openGroupEdit(group)}
                        className="bg-blue-100 text-blue-700 px-3 py-2 rounded-lg text-sm font-medium hover:bg-blue-200 transition-colors flex items-center justify-center"
                        title="Edit Group"
                      >
                        ✏️
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Discover Groups */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Discover Groups</h2>
        <div className="grid md:grid-cols-2 gap-6">
          {filteredGroups.map((group) => (
            <div key={group.id} className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center font-bold text-xl">
                      {group.name.substring(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{group.name}</h3>
                      <div className="flex items-center space-x-2 text-sm text-green-600">
                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                        <span>Active</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <p className="text-gray-600 text-sm mb-4">{group.description}</p>
                
                <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setMembersModalGroup(group);
                      setShowMembersModal(true);
                    }}
                    className="hover:text-teal-600 hover:underline transition-colors cursor-pointer"
                  >
                    👥 {group.member_count} members
                  </button>
                  <span>📍 {group.location}</span>
                </div>
                
                <div className="flex flex-wrap gap-2 mb-4">
                  {group.industry_tags.slice(0, 3).map((tag, index) => (
                    <span key={index} className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs">
                      #{tag}
                    </span>
                  ))}
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    {group.posts_count} active posts
                  </div>
                  <button
                    onClick={() => joinGroup(group.id)}
                    className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors"
                  >
                    Join Group
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Create Group Modal */}
      {showCreateGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-screen overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Create New Group</h2>
                <button
                  onClick={() => setShowCreateGroup(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Group Name *
                  </label>
                  <input
                    type="text"
                    value={newGroup.name}
                    onChange={(e) => setNewGroup({...newGroup, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                    placeholder="Enter group name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    value={newGroup.description}
                    onChange={(e) => setNewGroup({...newGroup, description: e.target.value})}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                    placeholder="Describe your group"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Group Type
                  </label>
                  <select
                    value={newGroup.group_type}
                    onChange={(e) => setNewGroup({...newGroup, group_type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                  >
                    <option value="public">Public</option>
                    <option value="private">Private</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Industry Tags
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {industryOptions.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => handleIndustryTagChange(tag)}
                        className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                          newGroup.industry_tags.includes(tag)
                            ? 'bg-teal-100 text-teal-800 border-2 border-teal-300'
                            : 'bg-gray-100 text-gray-700 border-2 border-transparent hover:bg-gray-200'
                        }`}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location
                  </label>
                  <input
                    type="text"
                    value={newGroup.location}
                    onChange={(e) => setNewGroup({...newGroup, location: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                    placeholder="City, Indonesia"
                  />
                </div>

                <div className="flex justify-end space-x-4">
                  <button
                    onClick={() => setShowCreateGroup(false)}
                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={createGroup}
                    disabled={!newGroup.name || !newGroup.description}
                    className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Create Group
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Group Feed Modal */}
      {showFeed && selectedGroup && (
        <GroupFeed
          group={selectedGroup}
          onClose={closeGroupFeed}
        />
      )}

      {/* Group Details Modal */}
      {showGroupDetails && selectedGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-screen overflow-y-auto">
            <div className="p-6">
              {/* Modal Header */}
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">{selectedGroup.name}</h2>
                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      selectedGroup.group_type === 'public' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-orange-100 text-orange-800'
                    }`}>
                      {selectedGroup.group_type === 'public' ? '🌍 Public Group' : '🔒 Private Group'}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setMembersModalGroup(selectedGroup);
                        setShowMembersModal(true);
                      }}
                      className="hover:text-teal-600 hover:underline transition-colors cursor-pointer"
                    >
                      👥 {selectedGroup.member_count || 0} members
                    </button>
                    {selectedGroup.location && <span>📍 {selectedGroup.location}</span>}
                  </div>
                </div>
                <button
                  onClick={closeGroupDetails}
                  className="text-gray-400 hover:text-gray-600 text-xl"
                >
                  ✕
                </button>
              </div>

              {/* Group Details Content */}
              <div className="space-y-8">
                {/* Description */}
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">📝 About This Group</h3>
                  <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {selectedGroup.description || 'No description provided.'}
                  </p>
                </div>

                {/* Group Information Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Group Details */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-4 text-lg">📊 Group Information</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-600">Group Type:</span>
                        <span className="text-gray-900 capitalize">{selectedGroup.group_type}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-600">Members:</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setMembersModalGroup(selectedGroup);
                            setShowMembersModal(true);
                          }}
                          className="text-teal-600 hover:text-teal-700 hover:underline transition-colors cursor-pointer font-semibold"
                        >
                          {selectedGroup.member_count || 0} people →
                        </button>
                      </div>
                      {selectedGroup.location && (
                        <div className="flex justify-between">
                          <span className="font-medium text-gray-600">Location:</span>
                          <span className="text-gray-900">{selectedGroup.location}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-600">Created:</span>
                        <span className="text-gray-900">{new Date(selectedGroup.created_at).toLocaleDateString()}</span>
                      </div>
                      {selectedGroup.created_by && (
                        <div className="flex justify-between">
                          <span className="font-medium text-gray-600">Admin:</span>
                          <span className="text-gray-900">{selectedGroup.admin_name || 'Group Admin'}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Industry Tags & Categories */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-4 text-lg">🏷️ Categories & Tags</h4>
                    {selectedGroup.industry_tags && selectedGroup.industry_tags.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {selectedGroup.industry_tags.map((tag, index) => (
                          <span 
                            key={index}
                            className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500">No tags specified</p>
                    )}
                  </div>
                </div>

                {/* Group Rules */}
                {selectedGroup.rules && selectedGroup.rules.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-4 text-lg">📋 Group Rules</h4>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <ul className="space-y-2">
                        {selectedGroup.rules.map((rule, index) => (
                          <li key={index} className="flex items-start space-x-2">
                            <span className="text-blue-600 font-bold">{index + 1}.</span>
                            <span className="text-gray-800">{rule}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/* Group Activity Stats */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-4 text-lg">📈 Group Activity</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setMembersModalGroup(selectedGroup);
                        setShowMembersModal(true);
                      }}
                      className="bg-blue-50 p-4 rounded-lg text-center hover:bg-blue-100 transition-colors cursor-pointer"
                    >
                      <div className="text-2xl font-bold text-blue-600">{selectedGroup.member_count || 0}</div>
                      <div className="text-sm text-blue-600 font-medium">Members</div>
                    </button>
                    <div className="bg-green-50 p-4 rounded-lg text-center">
                      <div className="text-2xl font-bold text-green-600">{selectedGroup.post_count || 0}</div>
                      <div className="text-sm text-green-600 font-medium">Posts</div>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg text-center">
                      <div className="text-2xl font-bold text-purple-600">{selectedGroup.active_discussions || 0}</div>
                      <div className="text-sm text-purple-600 font-medium">Discussions</div>
                    </div>
                    <div className="bg-orange-50 p-4 rounded-lg text-center">
                      <div className="text-2xl font-bold text-orange-600">
                        {selectedGroup.created_at ? Math.floor((new Date() - new Date(selectedGroup.created_at)) / (1000 * 60 * 60 * 24)) : 0}
                      </div>
                      <div className="text-sm text-orange-600 font-medium">Days Active</div>
                    </div>
                  </div>
                </div>

                {/* Group Membership Status */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-4 text-lg">👤 Your Membership</h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    {myGroups.some(g => g.id === selectedGroup.id) ? (
                      <div className="flex items-center space-x-3">
                        <span className="text-green-600 text-xl">✅</span>
                        <div>
                          <p className="font-medium text-green-800">You are a member of this group</p>
                          <p className="text-sm text-green-600">Access the group feed and participate in discussions</p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-3">
                        <span className="text-gray-600 text-xl">👤</span>
                        <div>
                          <p className="font-medium text-gray-800">You are not a member of this group</p>
                          <p className="text-sm text-gray-600">Join to access group content and discussions</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Modal Actions */}
              <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200 mt-8">
                <button
                  onClick={closeGroupDetails}
                  className="flex-1 px-6 py-3 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                >
                  Close
                </button>
                
                {/* Group Action Buttons */}
                {myGroups.some(g => g.id === selectedGroup.id) ? (
                  <div className="flex-1 flex space-x-3">
                    <button
                      onClick={() => {
                        closeGroupDetails();
                        openGroupFeed(selectedGroup);
                      }}
                      className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                    >
                      📰 Open Group Feed
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => joinGroup(selectedGroup.id)}
                    className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                  >
                    ➕ Join Group
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Group Modal */}
      <EditGroupModal
        isOpen={showEditGroup}
        onClose={closeGroupEdit}
        group={editingGroup}
        onSave={handleEditGroup}
      />

      {/* Group Members Modal */}
      <GroupMembersModal
        isOpen={showMembersModal}
        onClose={() => setShowMembersModal(false)}
        groupId={membersModalGroup?.id}
        groupName={membersModalGroup?.name}
      />
    </div>
  );
};

export default Groups;